import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, toastApiError } from "@/lib/api";
import { Avatar, AVATAR_IDS } from "@/components/casino/Avatar";

export const UsernameCard = ({ sessionId }) => {
  const [current, setCurrent] = useState(null);
  const [nextAt, setNextAt] = useState(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [avatar, setAvatar] = useState("chip");
  const pickAvatar = async (id) => {
    const prev = avatar;
    setAvatar(id);
    try { await api.post("/player/avatar", { session_id: sessionId, avatar: id }); toast.success("Avatar updated", { id: "avatar" }); }
    catch (e) { setAvatar(prev); toastApiError(toast, e, "Could not save avatar"); }
  };

  useEffect(() => {
    if (!sessionId) return;
    api.get("/player/username", { params: { session_id: sessionId } }).then((r) => {
      setCurrent(r.data.username);
      setName(r.data.username || "");
      setNextAt(r.data.can_change_at);
      setAvatar(r.data.avatar || "chip");
    }).catch(() => {});
  }, [sessionId]);

  const locked = nextAt && new Date(nextAt) > new Date();
  const valid = /^[A-Za-z0-9_]{3,16}$/.test(name);
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/player/username", { session_id: sessionId, username: name.trim() });
      setCurrent(r.data.username);
      setNextAt(r.data.can_change_at);
      toast.success(`You're on the board as ${r.data.username}`);
    } catch (e) { toastApiError(toast, e, "Could not save username"); } finally { setSaving(false); }
  };

  return (
    <section className="casino-frame rounded-2xl p-5 sm:p-6" data-testid="username-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3"><Avatar id={avatar} size={40} testid="username-avatar-preview" /><h3 className="font-casino text-xl text-gold-grad">Leaderboard profile</h3></div>
        <div className="font-mono-naevo text-xs text-white/60" data-testid="username-current">{current ? <>Showing as <span className="text-gold">{current}</span></> : "Not set · you show as a masked ID"}</div>
      </div>
      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <input value={name} onChange={(e) => setName(e.target.value.replace(/\s/g, ""))} maxLength={16} placeholder="e.g. GoldenBoot_9"
          className="flex-1 rounded-full border border-gold/30 bg-black/50 px-4 py-2.5 font-mono-naevo text-white outline-none focus:border-gold" data-testid="username-input" />
        <button onClick={save} disabled={saving || !valid || name === current || (locked && current)} className="btn-gold rounded-full px-6 py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-50" data-testid="username-save-btn">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} {current ? "Change name" : "Set name"}
        </button>
      </div>
      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-[0.25em] text-gold/70 mb-2">Pick your avatar</div>
        <div className="flex flex-wrap gap-2" data-testid="avatar-picker">
          {AVATAR_IDS.map((id) => (
            <button key={id} onClick={() => pickAvatar(id)} data-testid={`avatar-option-${id}`} aria-label={id}
              className={`rounded-full p-0.5 transition-transform hover:-translate-y-0.5 ${avatar === id ? "ring-2 ring-[#F7D77E] ring-offset-2 ring-offset-black" : "opacity-70 hover:opacity-100"}`}>
              <Avatar id={id} size={40} />
            </button>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-white/45 mt-3">3–16 letters, numbers or _ · must be unique · can change once a day
        {locked && current && <> · next change {new Date(nextAt).toLocaleString()}</>}</p>
    </section>
  );
};
